// The deterministic simulation (IMPLEMENTATION §0.1, §2.1). A pure object: state + compiled defs + a command
// log + an event buffer. No clock, no I/O; GameLoop (M5) decides when to tick.
//   apply(cmd)  dispatch-time apply (phase 0 of tick N+1), logged {t: tick, seq, cmd}, returns CmdResult
//   tick()      one 20 Hz tick (§2.4)
//   drainEvents() the events since the last drain (commands' events fan out the same frame)
//   hash()      FNV-1a of the canonical JSON of the whole state
//   replay()    rebuilds a run from an initial state and a log; equals the dispatch-applied run
// A long-lived app Simulation caps its log (`logCap`: the oldest entries go; such a log no longer replays from
// the initial state — tests and tools keep the default, uncapped).
import type { CmdResult, Command, LogEntry } from './commands.ts';
import { hashValue } from './core/hash.ts';
import type { Int } from './core/types.ts';
import { makeCtx } from './ctx.ts';
import type { SimDefs } from './defs.ts';
import type { SimEvent } from './events.ts';
import { cloneState } from './setup.ts';
import type { SimState } from './state.ts';
import { applyCommand } from './systems/commandSystem.ts';
import { runTick } from './tick.ts';

export class Simulation {
  readonly defs: SimDefs;
  readonly state: SimState;
  readonly log: LogEntry[] = [];
  private seq: Int = 0;
  private events: SimEvent[] = [];
  private readonly logCap: Int;

  /** Takes ownership of `state` (pass a clone to keep the original). `logCap` < 0 = unbounded. */
  constructor(defs: SimDefs, state: SimState, opts: { logCap?: Int } = {}) {
    this.defs = defs;
    this.state = state;
    this.logCap = opts.logCap ?? -1;
  }

  apply(cmd: Command): CmdResult {
    const copy = JSON.parse(JSON.stringify(cmd)) as Command;
    if (this.logCap >= 0 && this.log.length >= this.logCap)
      this.log.splice(0, this.log.length - this.logCap + 1);
    if (this.logCap !== 0) this.log.push({ t: this.state.tick, seq: this.seq, cmd: copy });
    this.seq += 1;
    return applyCommand(makeCtx(this.state, this.defs, this.events), copy);
  }

  tick(): void {
    runTick(makeCtx(this.state, this.defs, this.events));
  }

  advance(n: Int): void {
    for (let i = 0; i < n; i++) this.tick();
  }

  drainEvents(): SimEvent[] {
    const out = this.events;
    this.events = [];
    return out;
  }

  hash(): string {
    return hashValue(this.state);
  }

  /**
   * Replays `log` from a copy of `initial` up to tick `toTick`: every entry with t = N is applied (in seq
   * order) before tick N + 1 runs; entries logged at `toTick` itself are applied last.
   */
  static replay(
    defs: SimDefs,
    initial: SimState,
    log: readonly LogEntry[],
    toTick: Int,
  ): Simulation {
    const sim = new Simulation(defs, cloneState(initial));
    let i = 0;
    const flush = (): void => {
      while (i < log.length && (log[i] as LogEntry).t === sim.state.tick) {
        sim.apply((log[i] as LogEntry).cmd);
        i++;
      }
    };
    while (sim.state.tick < toTick) {
      flush();
      if (i < log.length && (log[i] as LogEntry).t < sim.state.tick) {
        throw new Error(`replay: log entry ${i} (t=${(log[i] as LogEntry).t}) is out of order`);
      }
      sim.tick();
    }
    flush();
    return sim;
  }
}
